import {
  addDays,
  differenceInMinutes,
  getDate,
  getMonth,
  isSameDay,
  setHours,
  startOfDay,
  startOfHour,
} from 'date-fns';
import {useAnimatedPopper} from 'platform/components';
import {Clickable, Icon, Show, Text, useDevice} from 'platform/foundation';
import {DateTimeFormat, DayMonthFormat, useDateTimeFormatter, useLocale} from 'platform/locale';
import styled, {useTheme} from 'styled-components';

import {FocusEvent, KeyboardEvent, MouseEvent, useState} from 'react';

import {clamp, isNil, isNotNil} from 'ramda';

import {TestDriveCalendarDrive, TestDriveCalendarVehicle} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_LAYOUT} from '../constants/CALENDAR_LAYOUT';
import {BUSINESS_HOURS_COUNT, TEST_DRIVE_CALENDAR} from '../constants/TEST_DRIVE_CALENDAR';
import {useDelayedTooltipClose} from '../hooks/useDelayedTooltipClose';
import {useSlotHover} from '../hooks/useSlotHover';
import {getStartColumnIndex, TestDriveCalendarSlot} from '../utils/getSlotFromPointer';
import {getTestDriveBarLayout} from '../utils/getTestDriveBarLayout';
import {getVehicleDriveSegments} from '../utils/getVehicleDriveSegments';
import {TestDriveEventBar} from './TestDriveEventBar';
import {HoveredTestDrive, TestDriveEventTooltip} from './TestDriveEventTooltip';
import {TestDriveVehicleColumn} from './TestDriveVehicleColumn';

interface TestDriveResourceCalendarProps extends RequiredTestIdProps {
  vehicles: TestDriveCalendarVehicle[];
  windowStart: string;
  rangeDays: number;
  onTestDriveClick: (drive: TestDriveCalendarDrive) => void;
  /** Omitted when the user may not create a drive — an empty slot is then silently inert. */
  onSlotClick?: (vehicle: TestDriveCalendarVehicle, slot: TestDriveCalendarSlot) => void;
}

const MIN_LANES = 1;
const HOUR_LABEL_DIGITS = 2;

const MINUTES_PER_HOUR = 60;
const PERCENT = 100;

/** Without hover the `+` can never be revealed, so a tap anywhere in a free slot creates. */
const NO_HOVER_MEDIA_QUERY = '(hover: none)';
const isHoverless = () => window.matchMedia?.(NO_HOVER_MEDIA_QUERY).matches ?? false;

const getRowHeight = (laneCount: number) =>
  Math.max(
    Math.max(laneCount, MIN_LANES) * CALENDAR_LAYOUT.ROW_LANE_HEIGHT +
      CALENDAR_LAYOUT.ROW_VERTICAL_PADDING,
    CALENDAR_LAYOUT.MIN_ROW_HEIGHT
  );

const Scroller = styled.div`
  /*
   * Inherited by everything that lines up with the sticky vehicle column, so the phone width is
   * a single media query rather than four call sites.
   */
  --test-drive-vehicle-column-width: ${CALENDAR_LAYOUT.VEHICLE_COLUMN_WIDTH}px;
  @media ${(props) => props.theme.devices.mobile} {
    --test-drive-vehicle-column-width: ${CALENDAR_LAYOUT.VEHICLE_COLUMN_WIDTH_MOBILE}px;
  }
  overflow-x: auto;
  overflow-y: clip;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.general.separator};
  border-radius: ${(props) => props.theme.radii.small};
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const Canvas = styled.div<{$bodyWidth: number; $isStretched: boolean}>`
  position: relative;
  min-width: calc(var(--test-drive-vehicle-column-width) + ${(props) => props.$bodyWidth}px);
  ${(props) => (props.$isStretched ? 'width: 100%;' : '')}
`;

const Row = styled.div`
  display: flex;
`;

const HeaderRow = styled(Row)`
  position: sticky;
  top: 0;
  z-index: ${CALENDAR_LAYOUT.STICKY_HEADER_Z_INDEX};
`;

const HeaderCorner = styled.div`
  flex: 0 0 auto;
  position: sticky;
  left: 0;
  z-index: ${CALENDAR_LAYOUT.STICKY_CORNER_Z_INDEX};
  box-sizing: border-box;
  width: var(--test-drive-vehicle-column-width);
  min-width: var(--test-drive-vehicle-column-width);
  background-color: ${(props) => props.theme.colors.general.white};
  border-right: 1px solid ${(props) => props.theme.colors.general.separator};
  border-bottom: 1px solid ${(props) => props.theme.colors.general.separator};
`;

const DayHeader = styled.div<{$width: number; $isToday: boolean; $isStretched: boolean}>`
  box-sizing: border-box;
  ${(props) =>
    props.$isStretched
      ? `flex: 1 1 0; min-width: ${props.$width}px;`
      : `width: ${props.$width}px; min-width: ${props.$width}px;`}
  border-right: 1px solid ${(props) => props.theme.colors.general.separator};
  border-bottom: 1px solid ${(props) => props.theme.colors.general.separator};
  background-color: ${(props) =>
    props.$isToday ? props.theme.colors.palettes.blue[10][100] : props.theme.colors.general.white};
`;

const DayHeaderLabel = styled.div`
  height: ${(props) => props.theme.getSize(12)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const WeekDayLabel = styled.div`
  position: sticky;
  left: var(--test-drive-vehicle-column-width);
  display: flex;
  align-items: center;
  width: max-content;
  height: ${(props) => props.theme.getSize(7)};
  padding: 0 ${(props) => props.theme.getSize(2)};
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const HourRow = styled.div`
  display: flex;
  height: ${CALENDAR_LAYOUT.HEADER_HOUR_HEIGHT}px;
`;

const HourCell = styled.div`
  box-sizing: border-box;
  width: ${CALENDAR_LAYOUT.HOUR_COLUMN_WIDTH}px;
  min-width: ${CALENDAR_LAYOUT.HOUR_COLUMN_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid ${(props) => props.theme.colors.general.separator};
`;

const RowBody = styled.div<{
  $width: number;
  $height: number;
  $dayCount: number;
  $hourCount: number;
  $isStretched: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  ${(props) =>
    props.$isStretched
      ? `flex: 1 1 0; min-width: ${props.$width}px;`
      : `width: ${props.$width}px; min-width: ${props.$width}px;`}
  height: ${(props) => props.$height}px;
  padding-top: 4px;
  border-bottom: 1px solid ${(props) => props.theme.colors.general.separator};
  background-image: repeating-linear-gradient(
      to right,
      ${(props) => props.theme.colors.general.separator} 0,
      ${(props) => props.theme.colors.general.separator} 1px,
      transparent 1px,
      transparent calc(100% / ${(props) => props.$dayCount})
    )
    ${(props) =>
      props.$hourCount > 0
        ? `,
    repeating-linear-gradient(
      to right,
      ${props.theme.colors.palettes.neutral[30][100]} 0,
      ${props.theme.colors.palettes.neutral[30][100]} 1px,
      transparent 1px,
      transparent calc(100% / ${props.$hourCount})
    )`
        : ''};
`;

/**
 * The grid lines are a CSS gradient, so there is no cell element to hang a hover state on — the
 * highlight is placed on the column the pointer resolved to, under the bars.
 */
const SlotHighlight = styled.div<{$left: number; $width: number}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$left}%;
  width: ${(props) => props.$width}%;
  background-color: ${(props) => props.theme.colors.palettes.neutral[20][100]};
  pointer-events: none;
`;

/** Stays mounted and focusable while hidden, so each row is one Tab stop for the keyboard. */
const SlotAddButton = styled.button<{$centre: number; $isVisible: boolean}>`
  position: absolute;
  top: 50%;
  left: ${(props) => props.$centre}%;
  z-index: ${CALENDAR_LAYOUT.SLOT_OVERLAY_Z_INDEX};
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  width: ${(props) => props.theme.getSize(CALENDAR_LAYOUT.SLOT_ADD_HIT_AREA)};
  height: ${(props) => props.theme.getSize(CALENDAR_LAYOUT.SLOT_ADD_HIT_AREA)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.$isVisible ? 'auto' : 'none')};
`;

// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const NowMarker = styled.div<{$fraction: number}>`
  position: absolute;
  top: 0;
  bottom: 0;
  /* Anchored as a fraction of the body region, so it tracks stretched columns too. */
  z-index: ${CALENDAR_LAYOUT.SLOT_OVERLAY_Z_INDEX};
  left: calc(
    var(--test-drive-vehicle-column-width) + ${(props) => props.$fraction} *
      (100% - var(--test-drive-vehicle-column-width))
  );
  width: 2px;
  background-color: ${(props) => props.theme.colors.severity.danger};
  pointer-events: none;
`;

export function TestDriveResourceCalendar(props: TestDriveResourceCalendarProps) {
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();
  const theme = useTheme();
  const isMobile = useDevice() === 'mobile';

  const [hoveredDrive, setHoveredDrive] = useState<HoveredTestDrive | null>(null);

  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: isMobile ? 'bottom-start' : 'right-start',
    gutter: 8,
    shiftOptions: {padding: 8, crossAxis: true},
    isFlipPrioritized: true,
    strategy: 'fixed',
    usePortal: true,
    isOutsideClickIgnored: true,
  });

  const {cancel: cancelTooltipClose, schedule: scheduleTooltipClose} = useDelayedTooltipClose(
    () => {
      setHoveredDrive(null);
      closePopper();
    }
  );

  // eslint-disable-next-line eag/no-external-date-time-format-functions -- platform/locale has no weekday format
  const weekdayFormatter = new Intl.DateTimeFormat(language, {weekday: 'short'});

  const firstDay = startOfDay(parseDate(props.windowStart));
  const days = Array.from({length: props.rangeDays}, (_, dayIndex) => addDays(firstDay, dayIndex));
  const businessHours = Array.from(
    {length: BUSINESS_HOURS_COUNT},
    (_, index) => TEST_DRIVE_CALENDAR.BUSINESS_START_HOUR + index
  );
  const isHourSubdivided = props.rangeDays === TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK;

  const dayColumnWidth = isHourSubdivided
    ? businessHours.length * CALENDAR_LAYOUT.HOUR_COLUMN_WIDTH
    : CALENDAR_LAYOUT.DAY_COLUMN_MIN_WIDTH;
  const bodyWidth = days.length * dayColumnWidth;

  const now = new Date();
  const nowDayIndex = days.findIndex((day) => isSameDay(day, now));
  /**
   * Clamped: outside 05:00-20:00 the raw ratio leaves 0..1 and drew the marker in a neighbouring
   * date's column. Minutes are counted so the line moves continuously, not on the hour.
   */
  const nowFractionOfDay = clamp(
    0,
    1,
    differenceInMinutes(now, startOfHour(setHours(now, TEST_DRIVE_CALENDAR.BUSINESS_START_HOUR))) /
      (businessHours.length * MINUTES_PER_HOUR)
  );
  const nowFraction = (nowDayIndex + nowFractionOfDay) / props.rangeDays;
  /*
   * 7-day view only. A 30-day column is one whole day, so the marker could only sit on the day
   * boundary, where it reads as the edge between yesterday and today and never moves.
   */
  const isNowMarkerVisible = isHourSubdivided && nowDayIndex >= 0;

  const {hoveredSlot, setHoveredSlot, readSlot, columnCount, getSlotAtColumn, handleSlotKeyDown} =
    useSlotHover({firstDay, rangeDays: props.rangeDays, isHourSubdivided});

  const getRowBodyHandlers = (
    vehicle: TestDriveCalendarVehicle,
    rowHoveredSlot: TestDriveCalendarSlot | null
  ) => ({
    onClick: (event: MouseEvent<HTMLDivElement>) => {
      if (isNil(props.onSlotClick) || !isHoverless()) {
        return;
      }

      props.onSlotClick(vehicle, readSlot(event));
    },
    onMouseMove: (event: MouseEvent<HTMLDivElement>) => {
      // Bars and the `+` sit above the row, so only the free area itself lands here.
      if (isNil(props.onSlotClick) || event.target !== event.currentTarget) {
        return;
      }

      const slot = readSlot(event);

      // Every pixel of travel would otherwise re-render all 50 rows.
      if (rowHoveredSlot?.columnIndex === slot.columnIndex) {
        return;
      }

      setHoveredSlot({vehicleId: vehicle.vehicleId, slot});
    },
    onMouseLeave: () => setHoveredSlot(null),
  });

  const getAddButtonHandlers = (
    vehicle: TestDriveCalendarVehicle,
    rowHoveredSlot: TestDriveCalendarSlot | null
  ) => ({
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      // A tap on touch would otherwise also reach the row's own create handler.
      event.stopPropagation();

      if (isNotNil(rowHoveredSlot)) {
        props.onSlotClick?.(vehicle, rowHoveredSlot);
      }
    },
    onFocus: (event: FocusEvent<HTMLButtonElement>) => {
      // Focus restored by a closing dialog must not drag the `+` to 05:00.
      if (isNil(rowHoveredSlot) && event.currentTarget.matches(':focus-visible')) {
        setHoveredSlot({vehicleId: vehicle.vehicleId, slot: getSlotAtColumn(0)});
      }
    },
    // Only clear this row: clearing another row's hover hides its `+` mid-click.
    onBlur: () =>
      setHoveredSlot((current) => (current?.vehicleId === vehicle.vehicleId ? null : current)),
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) =>
      handleSlotKeyDown(event, vehicle.vehicleId, rowHoveredSlot),
  });

  return (
    <>
      <Scroller data-testid={suffixTestId('calendar', props)}>
        <Canvas $bodyWidth={bodyWidth} $isStretched={!isHourSubdivided}>
          <HeaderRow>
            <HeaderCorner />
            {days.map((day) => (
              <DayHeader
                key={day.toISOString()}
                $width={dayColumnWidth}
                $isToday={isSameDay(day, now)}
                $isStretched={!isHourSubdivided}
              >
                {isHourSubdivided ? (
                  <WeekDayLabel>
                    <Text size="xSmall" alternative={isSameDay(day, now)} noWrap>
                      {`${weekdayFormatter.format(day)} `}
                      <DateTimeFormat format="dateShort" value={day} />
                    </Text>
                  </WeekDayLabel>
                ) : (
                  <DayHeaderLabel>
                    <Text size="xSmall" color="tertiary" noWrap>
                      {weekdayFormatter.format(day)}
                    </Text>
                    <Text size="xSmall" alternative={isSameDay(day, now)} noWrap>
                      <DayMonthFormat
                        format="dateShort"
                        value={{day: getDate(day), month: getMonth(day) + 1}}
                      />
                    </Text>
                  </DayHeaderLabel>
                )}
                <Show when={isHourSubdivided}>
                  <HourRow>
                    {businessHours.map((hour) => (
                      <HourCell key={hour}>
                        <Text size="xSmall" color="tertiary">
                          {String(hour).padStart(HOUR_LABEL_DIGITS, '0')}
                        </Text>
                      </HourCell>
                    ))}
                  </HourRow>
                </Show>
              </DayHeader>
            ))}
          </HeaderRow>

          {props.vehicles.map((vehicle) => {
            const segments = getVehicleDriveSegments(vehicle.testDrives);
            const laneCount = segments[0]?.laneCount ?? MIN_LANES;
            const rowHeight = getRowHeight(laneCount);
            const rowHoveredSlot =
              hoveredSlot?.vehicleId === vehicle.vehicleId ? hoveredSlot.slot : null;

            return (
              <Row key={vehicle.vehicleId}>
                <TestDriveVehicleColumn
                  vehicle={vehicle}
                  rowHeight={rowHeight}
                  data-testid={suffixTestId(`row-${vehicle.vehicleId}`, props)}
                />
                <RowBody
                  $width={bodyWidth}
                  $height={rowHeight}
                  $dayCount={props.rangeDays}
                  $hourCount={isHourSubdivided ? props.rangeDays * businessHours.length : 0}
                  $isStretched={!isHourSubdivided}
                  data-testid={suffixTestId(`slots-${vehicle.vehicleId}`, props)}
                  {...getRowBodyHandlers(vehicle, rowHoveredSlot)}
                >
                  <Show when={isNotNil(rowHoveredSlot)}>
                    <SlotHighlight
                      $left={((rowHoveredSlot?.columnIndex ?? 0) / columnCount) * PERCENT}
                      $width={PERCENT / columnCount}
                    />
                  </Show>
                  <Show when={isNotNil(props.onSlotClick)}>
                    <SlotAddButton
                      type="button"
                      $centre={(((rowHoveredSlot?.columnIndex ?? 0) + 0.5) / columnCount) * PERCENT}
                      $isVisible={isNotNil(rowHoveredSlot)}
                      aria-label={i18n.t('entity.testDrive.labels.createTestDriveAt', {
                        time: formatDateTime(
                          isHourSubdivided ? 'dateTimeShort' : 'dateShort',
                          rowHoveredSlot?.start ?? getSlotAtColumn(0).start
                        ),
                      })}
                      data-testid={suffixTestId(`addHint-${vehicle.vehicleId}`, props)}
                      {...getAddButtonHandlers(vehicle, rowHoveredSlot)}
                    >
                      <Icon value="lightbox/plus" size={4} color="text.tertiary" />
                    </SlotAddButton>
                  </Show>
                  {segments.map((segment) => {
                    const layout = getTestDriveBarLayout(
                      segment.start,
                      segment.end,
                      firstDay,
                      props.rangeDays
                    );

                    return (
                      <TestDriveEventBar
                        key={segment.key}
                        segment={segment}
                        leftPercent={layout.leftPercent}
                        widthPercent={layout.widthPercent}
                        isHovered={hoveredDrive?.segment.key === segment.key}
                        isDimmed={
                          isNotNil(rowHoveredSlot) &&
                          getStartColumnIndex(segment.start, firstDay, isHourSubdivided) ===
                            rowHoveredSlot.columnIndex
                        }
                        referenceRef={referenceRef}
                        onClick={props.onTestDriveClick}
                        onHoverStart={(hoveredSegment) => {
                          cancelTooltipClose();
                          setHoveredSlot(null);
                          setHoveredDrive({segment: hoveredSegment, vehicleTitle: vehicle.title});
                          openPopper();
                        }}
                        onHoverEnd={scheduleTooltipClose}
                        data-testid={suffixTestId(`drive-${segment.key}`, props)}
                      />
                    );
                  })}
                </RowBody>
              </Row>
            );
          })}

          <Show when={isNowMarkerVisible}>
            <NowMarker $fraction={nowFraction} />
          </Show>
        </Canvas>
      </Scroller>

      <Popper
        {...popperProps}
        floatingStyles={{...popperProps.floatingStyles, zIndex: theme.zIndices.TOOLTIP}}
      >
        <Clickable
          isTextSelectable
          onMouseEnter={cancelTooltipClose}
          onMouseLeave={scheduleTooltipClose}
          data-testid={suffixTestId('tooltipHoverArea', props)}
        >
          <TestDriveEventTooltip
            hovered={hoveredDrive}
            data-testid={suffixTestId('event', props)}
          />
        </Clickable>
      </Popper>
    </>
  );
}
